import React, { useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

import {
  GlobalResponse,
  SliderConfigs,
  Slug,
} from '../../../models/domain/interfaces';
import { CarouselNewsEventsWrapper } from './SliderNews.style';
import CarouselNewsEvents from '../../../components/carousel/carousel-news-events/CarouselNewsEvents';
import { MobilePaddingLessContentWrapper } from '../../../styles/shared';
import SectionHeader from '../../../components/section-header/SectionHeader';
import { singleNewsSliderConfigs } from '../../../utils/carousels';
import { Newsa } from '../../../models/auto-generated-strapi-types/types/Newsa';
import { getNews } from '../../../utils/api';
import { Pages } from '../../../models/domain/enum';
import { normalizeAttributesNewsEvents } from '../../../utils/event';

interface SliderNewsProps {
  mainTitle: string;
  title: string;
  mainTitleFontSize?: number;
  titleFontSize?: number;
  mainTitleMobileFontSize?: number;
  titleMobileFontSize?: number;
  mainTitleFontWeight?: number;
  mainTitleMobileFontWeight?: number;
  titleFontWeight?: number;
  titleMobileFontWeight?: number;
  news: Newsa[];
  sliderConfigs?: SliderConfigs;
  showButton?: boolean;
}

const SliderNews: React.FC<any> = ({
  data,
  translations,
}: {
  data: SliderNewsProps;
  translations: any;
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpointsMediaQuery.up('md'));
  const [, setRerender] = useState<number>(0); // Trick to force re-render

  useEffect(() => {
    setRerender(Date.now());
  }, []);

  const {
    news,
    sliderConfigs,
    title,
    mainTitle,
    titleFontSize,
    mainTitleFontSize,
    mainTitleMobileFontSize,
    titleMobileFontSize,
    mainTitleFontWeight,
    mainTitleMobileFontWeight,
    titleFontWeight,
    titleMobileFontWeight,
  } = data ?? {};

  const newsMapped: any = news?.map?.(({ id, attributes }) => {
    const image =
      typeof window === 'undefined' || isDesktop
        ? attributes?.thumbnail
        : attributes?.mobileThumbnail?.data
          ? attributes?.mobileThumbnail
          : attributes?.thumbnail;

    return {
      title: attributes?.title,
      description: attributes?.description,
      image,
      category: attributes?.category,
      overlayColor: attributes?.overlayColor,
      coverTextColor: attributes?.coverTextColor,
      date: attributes?.date,
      types: attributes?.types?.data,
      url: (attributes as any)?.url,
      button: id === -1 && attributes?.button,
      isLast: id === -1,
    };
  });

  return (
    <>
      {news && news.length > 0 && (
        <MobilePaddingLessContentWrapper>
          {mainTitle || title ? (
            <SectionHeader
              subtitle={mainTitle ?? ''}
              title={title ?? ''}
              titleFontSize={titleFontSize}
              mainTitleFontSize={mainTitleFontSize}
              titleMobileFontSize={titleMobileFontSize}
              mainTitleMobileFontSize={mainTitleMobileFontSize}
              mainTitleFontWeight={mainTitleFontWeight}
              mainTitleMobileFontWeight={mainTitleMobileFontWeight}
              titleFontWeight={titleFontWeight}
              titleMobileFontWeight={titleMobileFontWeight}
            />
          ) : (
            <></>
          )}
          <CarouselNewsEventsWrapper lessPadding={true}>
            <CarouselNewsEvents
              goToNewsEventsLink={
                data.showButton
                  ? data?.sliderConfigs?.alternativeLink ||
                    translations?.urls?.newsEntryPage
                  : undefined
              }
              showCarouselTitle={sliderConfigs?.showCarouselTitle}
              carouselTitle={
                translations?.pages?.newsEvents?.newsSlider?.newsCarouselTitle
              }
              goToNewsEventsText={
                data?.sliderConfigs?.alternativeLinkText ||
                translations?.pages?.home?.newsEventsSlider?.goToNews
              }
              cards={newsMapped}
              sliderConfigs={sliderConfigs ?? singleNewsSliderConfigs}
            />
          </CarouselNewsEventsWrapper>
        </MobilePaddingLessContentWrapper>
      )}
    </>
  );
};

export const getServerSideProps = async (
  section: any,
  translations: any,
  slugAnalyzed: Slug,
  globalLocale: GlobalResponse
) => {
  const getNewsParams: any = {
    locale: slugAnalyzed.locale,
    pageSize: 10,
    sort: ['date:desc'],
    datesFormat: translations?.date?.sliderNewsEvents?.format,
  };

  const types = section?.types?.data?.map((type: any) => type.id);
  let filters = {};

  if (section?.filter) {
    getNewsParams.filters = section?.filter;
  } else {
    if (types && types.length > 0) {
      filters = {
        types: {
          id: {
            $in: types,
          },
        },
      };
      getNewsParams.filters = filters;
    }
  }

  let news = (await getNews(getNewsParams))?.data;

  news =
    normalizeAttributesNewsEvents(
      news,
      'news',
      translations,
      globalLocale?.attributes?.newsAndEventsMerged || false,
      slugAnalyzed.locale
    ) || [];

  if (news) {
    if (
      slugAnalyzed.type === Pages.NEWS_EVENTS ||
      slugAnalyzed.type === Pages.NEWS_ENTRY_PAGE
    ) {
      if (!section?.sliderConfigs?.forceHideGoToArchive) {
        news.push({
          id: -1,
          attributes: {
            overlayColor: '#EFEFEF',
            button: {
              url:
                '/' +
                slugAnalyzed.locale +
                '/' +
                (section?.sliderConfigs?.alternativeLink ||
                  translations?.urls?.newsEntryPage),
              text:
                section?.sliderConfigs?.alternativeLinkText ||
                translations?.pages?.home?.newsEventsSlider?.goToNews,
            },
          },
        });
        section.showButton = true;
      } else {
        section.showButton = false;
      }
    } else {
      if (
        !section?.sliderConfigs?.forceHideGoToArchive &&
        slugAnalyzed.type !== Pages.PRODUCT
      ) {
        news.push({
          id: -1,
          attributes: {
            overlayColor: '#EFEFEF',
            button: {
              url:
                section?.sliderConfigs?.alternativeLink ||
                translations?.urls?.newsEntryPage,
              text:
                section?.sliderConfigs?.alternativeLinkText ||
                translations?.pages?.home?.newsEventsSlider?.goToNews,
            },
          },
        });
        section.showButton = false;
      } else {
        section.showButton = false;
      }
    }
  }

  section.news = news;
};

export default SliderNews;
